package io.horion.cv.components.preview

import androidx.compose.runtime.Composable
import io.horion.cv.api.datasource.cv.OrionCV
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.css.JustifyContent
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

private const val C6_PHOTO_CLIP_PATH  =
    "polygon(0% 0%, 100% 0%, 100% 91.9%, 83.3% 91.9%, 47% 100%, 33.1% 91.9%, 0% 91.9%)"
private const val C6_PHOTO_BOX_HEIGHT = "447px"

private const val C6_RIBBON_LEFT   = "-4px"
private const val C6_RIBBON_TOP    = "305px"
private const val C6_RIBBON_WIDTH  = "112px"
private const val C6_RIBBON_HEIGHT = "121px"

private const val BG_TEMPLATE_6 = "/cv6-bg.png"
private const val RIBBON_6 = "/cv6-ribbon.png"
private const val C6_PHOTO_BOX_WIDTH  = "281px"

@Composable
fun CvSixPreview(data: OrionCV) {
    val pi = data.personalInformation
    val validPersonalizedSections =
        data.personalizedSections.filter { section ->
            section.resume.isNotBlank() ||
                    section.started.isNotBlank() ||
                    section.ended.isNotBlank() ||
                    section.description.isNotBlank()
        }
    val orangeAccent = Color("#FF9800")
    val darkGray = Color("#333333")

    // --- FILTRAGE DES LISTES (Évite l'affichage des brouillons vides) ---
    val validExperiences = data.experiences.filter { it.post.isNotBlank() || it.enterprise.isNotBlank() }
    val validStages = data.stages.filter { it.post.isNotBlank() || it.enterprise.isNotBlank() }
    val validLearns = data.learns.filter { it.label.isNotBlank() }
    val validExtracurricular = data.extracurricularActivities.filter { it.name.isNotBlank() || it.enterprise.isNotBlank() }
    val validFormations = data.formations.filter { it.label.isNotBlank() || it.school.isNotBlank() }
    val validCertificates = data.certificates.filter { it.name.isNotBlank() }
    val validAchievements = data.achievements.filter { it.isNotBlank() }
    val validReferences = data.references.filter { it.name.isNotBlank() }

    val validCompetences = data.competences.filter { it.name.isNotBlank() }
    val validQualities = data.qualities.filter { it.isNotBlank() }
    val validSoftware = data.software.filter { it.name.isNotBlank() }
    val validHobbies = data.hobbies.filter { it.isNotBlank() }

    Div(attrs = {
        style {
            display(DisplayStyle.Flex)
            width(794.px)
            minHeight(1123.px)
            fontFamily("Inter", "sans-serif")
            position(Position.Relative)
            boxSizing("border-box")
            overflow("hidden")
            property("background-image", "url('$BG_TEMPLATE_6')")
            property("background-size", "794px 1123px")
            property("background-repeat", "no-repeat")
            property("background-position", "top left")
        }
    }) {
        // --- COLONNE GAUCHE (Contenu Principal) ---
        Div(attrs = {
            style {
                flex(1)
                minWidth(0.px)
                padding(40.px)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                boxSizing("border-box")
            }
        }) {
            // Header Nom & Titre
            Div(attrs = { style { marginBottom(40.px); display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween) } }) {
                Div(attrs = { style { minWidth(0.px) } }) {
                    H2(attrs = { style { margin(0.px); fontSize(16.px); fontWeight("normal"); color(darkGray); property("overflow-wrap", "anywhere"); property("word-break", "break-word") } }) { Text(pi.firstName) }
                    H1(attrs = { style { margin(0.px); fontSize(20.px); fontWeight("bold"); color(Color("#000000")); property("overflow-wrap", "anywhere"); property("word-break", "break-word") } }) { Text(pi.lastName.uppercase()) }
                    H3(attrs = { style { margin(0.px); fontSize(14.px); fontWeight("300"); color(Color("#777777")); property("overflow-wrap", "anywhere"); property("word-break", "break-word") } }) { Text(pi.bp.uppercase()) }
                }
                if (pi.qrCodePath.isNotBlank()) {
                    Img(src = pi.qrCodePath, attrs = { style { width(60.px); height(60.px); property("flex-shrink", "0") } })
                }
            }

            // PROFIL (HTML riche : gras/italique/souligné/alignement/listes)
            if (!data.profil.isNullOrBlank()) {
                Cv6MainTitle("PROFIL", orangeAccent)
                HtmlBlock(data.profil) {
                    fontSize(12.px); lineHeight("1.6"); color(darkGray); marginBottom(30.px)
                    property("overflow-wrap", "anywhere"); property("word-break", "break-word")
                }
            }

            if (validPersonalizedSections.isNotEmpty()) {
                validPersonalizedSections.forEach { section ->
                    Cv6PersonalizedSection(
                        section = section,
                        accent = orangeAccent,
                        darkGray = darkGray
                    )
                }
            }

            // EXPÉRIENCES PROFESSIONNELLES
            if (validExperiences.isNotEmpty()) {
                Cv6MainTitle("EXPÉRIENCES PROFESSIONNELLES", orangeAccent)
                validExperiences.forEach { exp ->
                    Cv6TimelineItem(
                        title    = exp.post,
                        period   = formatPeriod(exp.started, exp.ended),
                        company  = exp.enterprise,
                        city     = exp.city,
                        bullets  = exp.description,
                        accent   = orangeAccent,
                        darkGray = darkGray
                    )
                }
            }

            // STAGES
            if (validStages.isNotEmpty()) {
                Cv6MainTitle("STAGES", orangeAccent)
                validStages.forEach { stage ->
                    Cv6TimelineItem(
                        title    = stage.post,
                        period   = formatPeriod(stage.started, stage.ended),
                        company  = stage.enterprise,
                        city     = stage.city,
                        bullets  = if (stage.description.isNotBlank()) listOf(stage.description) else emptyList(),
                        accent   = orangeAccent,
                        darkGray = darkGray
                    )
                }
            }

            // COURS
            if (validLearns.isNotEmpty()) {
                Cv6MainTitle("COURS", orangeAccent)
                validLearns.forEach { learn ->
                    Cv6TimelineItem(
                        title    = learn.label,
                        period   = formatPeriod(learn.started, learn.ended),
                        company  = "",
                        city     = "",
                        bullets  = if (learn.description.isNotBlank()) listOf(learn.description) else emptyList(),
                        accent   = orangeAccent,
                        darkGray = darkGray
                    )
                }
            }

            // ACTIVITÉS EXTRA-SCOLAIRES
            if (validExtracurricular.isNotEmpty()) {
                Cv6MainTitle("ACTIVITÉS EXTRA-SCOLAIRES", orangeAccent)
                validExtracurricular.forEach { activity ->
                    Cv6TimelineItem(
                        title    = activity.name,
                        period   = formatPeriod(activity.started, activity.ended),
                        company  = activity.enterprise,
                        city     = activity.city,
                        bullets  = if (activity.description.isNotBlank()) listOf(activity.description) else emptyList(),
                        accent   = orangeAccent,
                        darkGray = darkGray
                    )
                }
            }

            // DIPLOMES (Grid de cartes)
            if (validFormations.isNotEmpty()) {
                Cv6MainTitle("DIPLOMES", orangeAccent)
                Div(attrs = {
                    style {
                        display(DisplayStyle.Flex)
                        flexWrap(FlexWrap.Wrap)
                        gap(15.px)
                    }
                }) {
                    validFormations.forEach { form ->
                        Div(attrs = {
                            style {
                                width(160.px)
                                border(1.px, LineStyle.Solid, orangeAccent)
                                borderRadius(4.px)
                                padding(10.px)
                                boxSizing("border-box")
                                property("overflow-wrap", "anywhere")
                                property("word-break", "break-word")
                            }
                        }) {
                            Div(attrs = { style { fontWeight("bold"); fontSize(11.px); color(orangeAccent); marginBottom(5.px) } }) { Text(form.label) }
                            Div(attrs = { style { fontSize(10.px); fontWeight("bold") } }) { Text(form.school) }
                            Div(attrs = { style { fontSize(9.px); color(Color("#999999")) } }) { Text("${form.city}/${extractYear(form.ended)}") }
                            if (form.description.isNotBlank()) {
                                Div(attrs = { style { fontSize(9.px); color(darkGray); marginTop(4.px) } }) { HtmlInline(form.description) }
                            }
                        }
                    }
                }
            }

            // CERTIFICATS (même style de carte que les diplômes)
            if (validCertificates.isNotEmpty()) {
                Cv6MainTitle("CERTIFICATS", orangeAccent)
                Div(attrs = { style { display(DisplayStyle.Flex); flexWrap(FlexWrap.Wrap); gap(15.px) } }) {
                    validCertificates.forEach { cert ->
                        Div(attrs = {
                            style {
                                width(160.px)
                                border(1.px, LineStyle.Solid, orangeAccent)
                                borderRadius(4.px)
                                padding(10.px)
                                boxSizing("border-box")
                                property("overflow-wrap", "anywhere")
                                property("word-break", "break-word")
                            }
                        }) {
                            Div(attrs = { style { fontWeight("bold"); fontSize(11.px); color(orangeAccent); marginBottom(5.px) } }) { Text(cert.name) }
                            Div(attrs = { style { fontSize(9.px); color(Color("#999999")) } }) { Text(extractYear(cert.date)) }
                            if (cert.description.isNotBlank()) {
                                Div(attrs = { style { fontSize(9.px); color(darkGray); marginTop(4.px) } }) { HtmlInline(cert.description) }
                            }
                        }
                    }
                }
            }

            // RÉALISATIONS
            if (validAchievements.isNotEmpty()) {
                Cv6MainTitle("RÉALISATIONS", orangeAccent)
                Ul(attrs = { style { margin(0.px); paddingLeft(15.px); fontSize(11.px); color(darkGray); marginBottom(20.px) } }) {
                    validAchievements.forEach { item -> Li(attrs = { style { property("overflow-wrap", "anywhere"); property("word-break", "break-word") } }) { HtmlInline(item) } }
                }
            }

            // RÉFÉRENCES
            if (validReferences.isNotEmpty()) {
                Cv6MainTitle("RÉFÉRENCES", orangeAccent)
                validReferences.forEach { ref ->
                    Div(attrs = { style { marginBottom(12.px) } }) {
                        Div(attrs = { style { fontWeight("bold"); fontSize(12.px); color(Color("#000000")); property("overflow-wrap", "anywhere"); property("word-break", "break-word") } }) { Text(ref.name) }
                        val subtitleParts = listOfNotNull(ref.enterprise.takeIf { it.isNotBlank() }, ref.city.takeIf { it.isNotBlank() })
                        if (subtitleParts.isNotEmpty()) {
                            Div(attrs = { style { color(orangeAccent); fontWeight("bold"); fontSize(11.px); marginTop(2.px); property("overflow-wrap", "anywhere"); property("word-break", "break-word") } }) { Text(subtitleParts.joinToString(" · ")) }
                        }
                        val contactParts = listOfNotNull(ref.phone.takeIf { it.isNotBlank() }, ref.email.takeIf { it.isNotBlank() })
                        if (contactParts.isNotEmpty()) {
                            Div(attrs = { style { fontSize(10.px); color(darkGray); marginTop(2.px); property("overflow-wrap", "anywhere"); property("word-break", "break-word") } }) { Text(contactParts.joinToString("   •   ")) }
                        }
                    }
                }
            }

            // SIGNATURE
            data.signature?.let { sig ->
                if (sig.city.isNotBlank() || sig.date.isNotBlank() || sig.image.isNotBlank()) {
                    Div(attrs = { style { marginTop(20.px); display(DisplayStyle.Flex); justifyContent(JustifyContent.FlexEnd) } }) {
                        Div(attrs = { style { textAlign(TextAlign.Right) } }) {
                            val line = listOfNotNull(sig.city.takeIf { it.isNotBlank() }, sig.date.takeIf { it.isNotBlank() })
                            if (line.isNotEmpty()) {
                                Div(attrs = { style { fontSize(10.px); color(Color("#999999")) } }) { Text(line.joinToString(", ")) }
                            }
                            if (sig.image.isNotBlank()) {
                                Img(src = sig.image, attrs = { style { height(50.px); marginTop(4.px) } })
                            }
                        }
                    }
                }
            }
        }

        // --- COLONNE DROITE (Sidebar) ---
        Div(attrs = {
            style {
                property("width", C6_PHOTO_BOX_WIDTH)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                flexShrink(0)
                boxSizing("border-box")
            }
        }) {
            // Zone photo : la silhouette + le ruban orange par défaut sont déjà
            Div(attrs = { style {
                position(Position.Relative)
                width(100.percent)
                property("height", C6_PHOTO_BOX_HEIGHT)
                flexShrink(0)
            } }) {
                if (pi.photo.isNotBlank()) {
                    Div(attrs = { style {
                        width(100.percent); height(100.percent); overflow("hidden")
                        property("clip-path", C6_PHOTO_CLIP_PATH)
                    } }) {
                        Img(src = pi.photo, attrs = {
                            style {
                                width(100.percent); height(100.percent); property("object-fit", "cover")
                            }
                        })
                    }
                }
                Img(src = RIBBON_6, attrs = { style {
                    position(Position.Absolute)
                    property("left", C6_RIBBON_LEFT)
                    property("top", C6_RIBBON_TOP)
                    property("width", C6_RIBBON_WIDTH)
                    property("height", C6_RIBBON_HEIGHT)
                    zIndex(2)
                } })
            }


            Div(attrs = { style { padding(25.px); minWidth(0.px) } }) {

                // INFORMATIONS
                val hasBirth    = pi.birth.isNotBlank()
                val hasNat      = pi.nationality.isNotBlank()
                val hasMarital  = pi.maritalStatus.isNotBlank()
                val hasLicense  = pi.drivingLicense.isNotBlank()
                if (hasBirth || hasNat || hasMarital || hasLicense) {
                    Cv6SidebarTitle("INFORMATIONS")
                    if (hasBirth)   Cv6SidebarRow("Né le : ${pi.birth}")
                    if (hasNat)     Cv6SidebarRow("Nat. : ${pi.nationality}")
                    if (hasMarital) Cv6SidebarRow("Sit. fam. : ${pi.maritalStatus}")
                    if (hasLicense) Cv6SidebarRow("Permis : ${pi.drivingLicense}")
                }

                // CONTACTS
                val location = listOfNotNull(
                    pi.city.takeIf { it.isNotBlank() },
                    pi.state.takeIf { it.isNotBlank() },
                    pi.country.takeIf { it.isNotBlank() }
                ).joinToString(", ")
                val hasWebsite = pi.siteWeb?.second?.isNotBlank() == true
                val hasLinks   = pi.links.any { it.second.isNotBlank() }
                if (pi.email.isNotBlank() || pi.phone.isNotBlank() || location.isNotBlank() || hasWebsite || hasLinks) {
                    Cv6SidebarTitle("CONTACTS")
                    if (pi.email.isNotBlank()) Cv6SidebarRow(pi.email)
                    if (pi.phone.isNotBlank()) Cv6SidebarRow(pi.phone)
                    if (location.isNotBlank()) Cv6SidebarRow(location)
                    pi.siteWeb?.let { (_, url) -> if (url.isNotBlank()) Cv6SidebarLinkRow(url) }
                    pi.links.forEach { (_, url) -> if (url.isNotBlank()) Cv6SidebarLinkRow(url) }
                }

                // COMPÉTENCES (Italique Orange)
                if (validCompetences.isNotEmpty()) {
                    Cv6SidebarTitle("COMPÉTENCES")
                    validCompetences.forEach { comp -> Cv6SidebarAccentItem(comp.name, orangeAccent) }
                }

                // QUALITÉS (même style que compétences)
                if (validQualities.isNotEmpty()) {
                    Cv6SidebarTitle("QUALITÉS")
                    validQualities.forEach { quality -> Cv6SidebarAccentItem(quality, orangeAccent) }
                }

                // LOGICIELS (même style que compétences)
                if (validSoftware.isNotEmpty()) {
                    Cv6SidebarTitle("LOGICIELS")
                    validSoftware.forEach { sw -> Cv6SidebarAccentItem(sw.name, orangeAccent) }
                }

                // HOBBIES (Tableau sombre)
                if (validHobbies.isNotEmpty()) {
                    Cv6SidebarTitle("HOBBIES")
                    Div(attrs = { style {
                        backgroundColor(darkGray); color(Color("#FFFFFF")); padding(10.px)
                        display(DisplayStyle.Grid); property("grid-template-columns", "1fr 1fr"); gap(10.px)
                        fontSize(11.px); textAlign(TextAlign.Center)
                    } }) {
                        validHobbies.forEach { Div(attrs = { style { property("overflow-wrap", "anywhere"); property("word-break", "break-word") } }) { Text(it) } }
                    }
                }
            }
        }
    }
}

@Composable
private fun Cv6MainTitle(title: String, accent: CSSColorValue) {
    Div(attrs = { style { marginBottom(20.px); marginTop(10.px) } }) {
        H3(attrs = { style { margin(0.px); fontSize(16.px); fontWeight("bold"); color(Color("#000000")) } }) { Text(title) }
        Div(attrs = { style { width(40.px); height(3.px); backgroundColor(accent); marginTop(5.px) } })
    }
}

// Item générique pour Expériences / Stages / Cours / Activités
@Composable
private fun Cv6TimelineItem(
    title:    String,
    period:   String,
    company:  String,
    city:     String,
    bullets:  List<String>,
    accent:   CSSColorValue,
    darkGray: CSSColorValue
) {
    Div(attrs = { style { marginBottom(20.px); minWidth(0.px) } }) {
        Div(attrs = { style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); flexWrap(FlexWrap.Wrap) } }) {
            Span(attrs = { style { fontWeight("bold"); fontSize(13.px); property("overflow-wrap", "anywhere"); property("word-break", "break-word"); minWidth(0.px) } }) { Text(title) }
            if (period.isNotBlank()) {
                Span(attrs = { style { margin(0.px, 10.px); color(Color("#999999")) } }) { Text("|") }
                Span(attrs = { style { fontSize(11.px); color(Color("#999999")); property("white-space", "nowrap") } }) { Text(period) }
            }
        }
        if (company.isNotBlank()) {
            Div(attrs = { style { color(accent); fontWeight("bold"); fontSize(12.px); marginBottom(2.px); property("overflow-wrap", "anywhere"); property("word-break", "break-word") } }) { Text(company) }
        }
        if (city.isNotBlank()) {
            Div(attrs = { style { fontSize(10.px); fontStyle(FontStyle.Italic); color(Color("#999999")); marginBottom(5.px); property("overflow-wrap", "anywhere"); property("word-break", "break-word") } }) { Text(city) }
        }
        if (bullets.isNotEmpty()) {
            Ul(attrs = { style { margin(0.px); paddingLeft(15.px); fontSize(11.px); color(darkGray) } }) {
                bullets.forEach { item -> Li(attrs = { style { property("overflow-wrap", "anywhere"); property("word-break", "break-word") } }) { HtmlInline(item) } }
            }
        }
    }
}

@Composable
private fun Cv6SidebarTitle(title: String) {
    H3(attrs = {
        style {
            fontSize(15.px); fontWeight("bold"); marginTop(30.px); marginBottom(15.px)
            property("text-transform", "uppercase")
        }
    }) { Text(title) }
}

@Composable
private fun Cv6SidebarRow(value: String) {
    Div(attrs = { style { marginBottom(8.px); fontSize(11.px); color(Color("#666666")); property("overflow-wrap", "anywhere"); property("word-break", "break-word") } }) {
        Text(value)
    }
}

@Composable
private fun Cv6SidebarLinkRow(url: String) {
    Div(attrs = { style { marginBottom(8.px); fontSize(11.px); property("overflow-wrap", "anywhere"); property("word-break", "break-word") } }) {
        A(href = url, attrs = {
            attr("target", "_blank"); attr("rel", "noopener noreferrer")
            style { color(Color("#666666")); property("text-decoration", "underline") }
        }) { Text(url) }
    }
}

@Composable
private fun Cv6SidebarAccentItem(text: String, accent: CSSColorValue) {
    Div(attrs = { style {
        color(accent); fontStyle(FontStyle.Italic); fontWeight("bold")
        fontSize(13.px); marginBottom(10.px); property("overflow-wrap", "anywhere"); property("word-break", "break-word")
    } }) { Text(text) }
}

@Composable
private fun Cv6PersonalizedSection(
    section: io.horion.cv.api.datasource.cv.Section,
    accent: CSSColorValue,
    darkGray: CSSColorValue
) {
    val title = section.title
        .ifBlank { "RUBRIQUE PERSONNALISÉE" }

    Cv6MainTitle(title, accent)

    Div(attrs = {
        style {
            property("width", "100%")
            property("max-width", "100%")
            property("min-width", "0")
            property("box-sizing", "border-box")
            property("margin-bottom", "20px")
            property("overflow", "hidden")
        }
    }) {
        if (section.resume.isNotBlank()) {
            Div(attrs = {
                style {
                    property("width", "100%")
                    property("max-width", "100%")
                    property("min-width", "0")
                    property("font-size", "12px")
                    property("font-weight", "700")
                    property("color", Color("#000000"))
                    property("line-height", "1.4")
                    property("white-space", "normal")
                    property("overflow-wrap", "anywhere")
                    property("word-break", "break-word")
                }
            }) {
                Text(section.resume)
            }
        }

        if (
            section.started.isNotBlank() ||
            section.ended.isNotBlank()
        ) {
            Div(attrs = {
                style {
                    property("width", "100%")
                    property("max-width", "100%")
                    property("min-width", "0")
                    property("font-size", "10px")
                    property("color", Color("#999999"))
                    property("line-height", "1.3")
                    property("margin-top", "3px")
                    property("margin-bottom", "5px")
                    property("white-space", "normal")
                    property("overflow-wrap", "anywhere")
                    property("word-break", "break-word")
                }
            }) {
                Text(
                    listOf(
                        section.started,
                        section.ended
                    )
                        .filter { it.isNotBlank() }
                        .joinToString(" — ")
                )
            }
        }

        if (section.description.isNotBlank()) {
            Div(attrs = {
                style {
                    property("width", "100%")
                    property("max-width", "100%")
                    property("min-width", "0")
                    property("font-size", "11px")
                    property("color", darkGray)
                    property("line-height", "1.6")
                    property("white-space", "normal")
                    property("overflow-wrap", "anywhere")
                    property("word-break", "break-word")
                    property("overflow", "hidden")
                }
            }) {
                HtmlBlock(section.description) {
                    property("width", "100%")
                    property("max-width", "100%")
                    property("min-width", "0")
                    property("font-size", "11px")
                    property("color", darkGray)
                    property("line-height", "1.6")
                    property("white-space", "normal")
                    property("overflow-wrap", "anywhere")
                    property("word-break", "break-word")
                }
            }
        }
    }
}